import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Empty,
  Modal,
  Radio,
  Select,
  InputNumber,
  Collapse,
  Input,
  Grid,
} from 'antd';
import { AppstoreOutlined, RightOutlined } from '@ant-design/icons';
import { api } from '../lib/api';
import type { App, Organization } from '../types';
import AppCard, { getAutoColor } from '../components/apps/AppCard';
import AppGroup from '../components/apps/AppGroup';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const LOCAL_TRIAL_OPTION = '__local_trial__';
type BillingScope = 'personal' | 'organization';

function formatDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
}

export default function AppsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const cardGutter = isMobile ? 12 : 20;
  const sectionPadding = isMobile ? 8 : 16;

  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [checkoutApp, setCheckoutApp] = useState<App | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [billingScope, setBillingScope] = useState<BillingScope>('personal');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [seatQuantity, setSeatQuantity] = useState(1);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [trialDays, setTrialDays] = useState(14);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [requestingIds, setRequestingIds] = useState<Set<string>>(new Set());
  const [requestedIds, setRequestedIds] = useState<Set<string>>(new Set());

  const normalizeForSearch = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  useEffect(() => {
    loadApps();
    loadOrganizations();
    loadFavorites();
    loadTrialConfig();
    const showWelcome = sessionStorage.getItem('annea_portal_show_welcome') === '1';
    if (showWelcome) {
      setWelcomeOpen(true);
      sessionStorage.removeItem('annea_portal_show_welcome');
    }
  }, []);

  useEffect(() => {
    const planAppId = searchParams.get('plans');
    if (!planAppId || apps.length === 0) return;
    const app = apps.find((entry) => entry.id === planAppId);
    if (!app) return;
    openPlans(app);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('plans');
      return next;
    }, { replace: true });
  }, [apps, searchParams, setSearchParams]);

  useEffect(() => {
    if (!selectedOrgId) return;
    const selectedOrg = orgs.find((org) => org.id === selectedOrgId);
    if (selectedOrg?.manualInvoicing) {
      setSelectedOrgId(null);
    }
  }, [orgs, selectedOrgId]);

  const formatAmount = (amount: number | null, currency?: string) => {
    if (amount === null || amount === undefined) return '-';
    const code = (currency || 'usd').toUpperCase();
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  };

  const loadApps = async () => {
    try {
      const data = await api.apps.list();
      setApps(data.apps.filter((app: App) => app.isActive));
    } catch {
      message.error('Failed to load apps');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const data = await api.organizations.list();
      setOrgs(data.organizations || []);
    } catch {
      setOrgs([]);
    }
  };

  const loadFavorites = async () => {
    try {
      const data = await api.user.favorites();
      setFavoriteIds(data.favorites || []);
    } catch {
      setFavoriteIds([]);
    }
  };

  const openCheckout = (app: App) => {
    if (app.isAdminGranted) return;
    const adminOrgs = orgs.filter((org) => org.role === 'OWNER' || org.role === 'ADMIN');
    const defaultOrg = adminOrgs.find((org) => !org.manualInvoicing) || adminOrgs[0];
    const defaultScope: BillingScope = defaultOrg ? 'organization' : 'personal';
    const canTrial = defaultScope === 'organization' ? app.orgTrialEligible : app.trialEligible;

    setCheckoutApp(app);
    setBillingScope(defaultScope);
    setSelectedOrgId(defaultScope === 'organization' ? defaultOrg?.id || null : null);
    setSelectedPriceId(canTrial ? LOCAL_TRIAL_OPTION : app.prices?.[0]?.id || null);
    setSeatQuantity(1);
    setCheckoutOpen(true);
  };

  const openPlans = (app: App) => {
    if (app.isAdminGranted) return;
    const adminOrgs = orgs.filter((org) => org.role === 'OWNER' || org.role === 'ADMIN');
    const defaultOrg = adminOrgs.find((org) => !org.manualInvoicing) || adminOrgs[0];
    const defaultScope: BillingScope = defaultOrg ? 'organization' : 'personal';
    setCheckoutApp(app);
    setBillingScope(defaultScope);
    setSelectedOrgId(defaultScope === 'organization' ? defaultOrg?.id || null : null);
    setSelectedPriceId(app.prices?.[0]?.id || null);
    setSeatQuantity(1);
    setCheckoutOpen(true);
  };

  const loadTrialConfig = async () => {
    try {
      const data = await api.apps.config();
      if (Number.isFinite(Number(data?.trialDays))) {
        setTrialDays(Number(data.trialDays));
      }
    } catch {
      setTrialDays(14);
    }
  };

  const handleCheckout = async () => {
    if (!checkoutApp || !selectedPriceId) {
      message.error('Select an option to continue');
      return;
    }

    try {
      setCheckoutLoading(true);
      if (selectedPriceId === LOCAL_TRIAL_OPTION) {
        if (billingScope === 'organization') {
          if (!selectedOrgId) {
            message.error('Select organization to continue');
            return;
          }
          const selectedOrg = orgs.find((org) => org.id === selectedOrgId);
          if (selectedOrg?.manualInvoicing) {
            message.error('This organization uses manual invoicing. Contact support.');
            return;
          }
          await api.subscriptions.startTrial(
            checkoutApp.id,
            selectedOrgId,
            seatQuantity
          );
          message.success(`Company ${trialDays}-day trial started`);
          setCheckoutOpen(false);
          await Promise.all([loadApps(), loadFavorites()]);
          return;
        }
        await api.subscriptions.startTrial(checkoutApp.id);
        message.success(`${trialDays}-day trial started`);
        setCheckoutOpen(false);
        await Promise.all([loadApps(), loadFavorites()]);
        return;
      }
      const effectiveOrgId = billingScope === 'organization' ? selectedOrgId : null;
      if (effectiveOrgId) {
        const selectedOrg = orgs.find((org) => org.id === effectiveOrgId);
        if (selectedOrg?.manualInvoicing) {
          message.error('This organization uses manual invoicing. Contact support.');
          return;
        }
      }
      const response = await api.subscriptions.checkout(
        checkoutApp.id,
        selectedPriceId,
        effectiveOrgId || undefined,
        effectiveOrgId ? seatQuantity : undefined
      );
      if (response?.url) {
        window.location.href = response.url;
        return;
      }
      message.error('Failed to start checkout');
    } catch (error: any) {
      message.error(error.message || 'Failed to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const filteredApps = useMemo(() => {
    const needle = normalizeForSearch(searchText.trim());
    if (!needle) {
      return apps;
    }
    const matchedIds = new Set(
      apps
        .filter((app) => normalizeForSearch(app.name).includes(needle))
        .map((app) => app.id)
    );

    const matchedCatalogGroupIds = new Set(
      apps.filter((app) => app.isCatalogGroup && matchedIds.has(app.id)).map((app) => app.id)
    );
    for (const app of apps) {
      if (app.parentId && matchedCatalogGroupIds.has(app.parentId)) matchedIds.add(app.id);
    }

    // Keep parent containers for matched child apps so grouped results stay visible.
    for (const app of apps) {
      if (matchedIds.has(app.id) && app.parentId) {
        matchedIds.add(app.parentId);
      }
    }

    return apps.filter((app) => matchedIds.has(app.id));
  }, [apps, searchText]);

  const { groups, standaloneApps } = useMemo(() => {
    const childrenByParent = new Map<string, App[]>();
    const standalone: App[] = [];

    filteredApps.forEach((app) => {
      if (app.parentId) {
        const list = childrenByParent.get(app.parentId) || [];
        list.push(app);
        childrenByParent.set(app.parentId, list);
      } else {
        standalone.push(app);
      }
    });

    const groupedParents = standalone.filter((app) => childrenByParent.has(app.id));
    const standaloneLeaf = standalone.filter((app) => !app.isCatalogGroup && !childrenByParent.has(app.id));

    const groupList = groupedParents
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
      .map((parent) => ({
        parent,
        children: (childrenByParent.get(parent.id) || []).sort(
          (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
        ),
      }));

    return {
      groups: groupList,
      standaloneApps: standaloneLeaf.sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
      ),
    };
  }, [filteredApps]);

  const isSubscribed = (app?: App | null) => Boolean(app?.isSubscribed);
  const isAccessible = (app?: App | null) => Boolean(app?.isSubscribed || app?.isFree);
  const groupParentIds = useMemo(() => new Set(groups.map((group) => group.parent.id)), [groups]);
  const isGroupApp = (app: App) => groupParentIds.has(app.id);

  /** Resolve effective icon color for any app (inherits from parent group) */
  const appIconColorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of groups) {
      const parentColor = group.parent.iconColor || getAutoColor(group.parent.id);
      map.set(group.parent.id, parentColor);
      for (const child of group.children) {
        map.set(child.id, parentColor);
      }
    }
    return map;
  }, [groups]);

  const openAppLink = (app: App) => {
    window.open(app.link, '_blank', 'noopener,noreferrer');
  };

  const isFavorite = (app: App) => favoriteIds.includes(app.id);

  const toggleFavorite = (app: App) => {
    const exists = favoriteIds.includes(app.id);
    if (exists) {
      api.user.removeFavorite(app.id)
        .then(() => setFavoriteIds((prev) => prev.filter((id) => id !== app.id)))
        .catch(() => message.error('Failed to remove favorite'));
    } else {
      api.user.addFavorite(app.id)
        .then(() => setFavoriteIds((prev) => [...prev, app.id]))
        .catch(() => message.error('Failed to add favorite'));
    }
  };

  const requestAccess = async (app: App) => {
    setRequestingIds((prev) => new Set(prev).add(app.id));
    try {
      const result = await api.apps.requestAccess(app.id);
      setRequestedIds((prev) => new Set(prev).add(app.id));
      message.success(result.message);
    } catch (error: any) { message.error(error.message || 'Could not send your access request. Please try again.'); }
    finally { setRequestingIds((prev) => { const next = new Set(prev); next.delete(app.id); return next; }); }
  };

  const renderSubscribeButton = (app: App) => {
    if (app.isAdminGranted) return !isSubscribed(app) ? (
      <Button type="link" loading={requestingIds.has(app.id)} disabled={requestedIds.has(app.id)}
        onClick={(event) => { event.stopPropagation(); void requestAccess(app); }}>
        {requestedIds.has(app.id) ? 'Request sent' : 'Request access'}
      </Button>
    ) : null;
    if (app.isAnneaOnly || app.isFree || isSubscribed(app)) {
      return null;
    }

    return (
      <Space>
        {!app.isSubscribed && app.hasOrgSubscription && isGroupApp(app) && (
          <Button
            type="primary"
            style={{ color: '#684c6b', background: '#e2d5f5', borderColor: '#e2d5f5' }}
            disabled
          >
            Org Sub
          </Button>
        )}
        {(app.trialEligible || app.orgTrialEligible) && (
          <Button
            icon={<RightOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              openCheckout(app);
              setSelectedPriceId(LOCAL_TRIAL_OPTION);
            }}
          >
            Start Trial
          </Button>
        )}
        <Button
          type="primary"
          icon={<RightOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            openCheckout(app);
          }}
          disabled={!app.prices || app.prices.length === 0}
        >
          View plans
        </Button>
      </Space>
    );
  };

  const trialState = useMemo(() => {
    if (!checkoutApp) {
      return {
        canStartTrial: false,
        hasActiveTrial: false,
        activeTrialEnd: null as string | null,
        hasExpiredTrial: false,
      };
    }

    const canStartTrial =
      billingScope === 'organization'
        ? Boolean(checkoutApp.orgTrialEligible)
        : Boolean(checkoutApp.trialEligible);
    const scopedTrialFlag =
      billingScope === 'organization'
        ? checkoutApp.hasOrganizationTrial
        : checkoutApp.hasPersonalTrial;
    const scopedTrialEnd =
      billingScope === 'organization'
        ? checkoutApp.organizationTrialEndsAt
        : checkoutApp.personalTrialEndsAt;
    const hasActiveTrial =
      scopedTrialFlag === undefined
        ? Boolean(checkoutApp.isTrial)
        : Boolean(scopedTrialFlag);
    const activeTrialEnd = formatDate(scopedTrialEnd || checkoutApp.trialEndsAt);
    const hasExpiredTrial =
      !hasActiveTrial &&
      !canStartTrial;

    return {
      canStartTrial,
      hasActiveTrial,
      activeTrialEnd,
      hasExpiredTrial,
    };
  }, [billingScope, checkoutApp]);

  const renderPlanButton = (app: App) => app.isAdminGranted ? <Text type="secondary">Admin-granted access</Text> : (
    <Button
      type="link"
      onClick={(e) => {
        e.stopPropagation();
        openPlans(app);
      }}
      disabled={!app.prices || app.prices.length === 0}
    >
      View plans
    </Button>
  );

  const catalogGroups = groups.filter((group) => group.parent.isCatalogGroup);
  const licensingGroups = groups.filter((group) => !group.parent.isCatalogGroup);
  const anneaGroups = licensingGroups.filter((group) => group.parent.isAnneaOnly);
  const subscribedGroups = licensingGroups.filter(
    (group) => !group.parent.isAnneaOnly && isSubscribed(group.parent) && !group.parent.isFree
  );
  const freeGroups = licensingGroups.filter((group) => !group.parent.isAnneaOnly && group.parent.isFree);
  const unsubscribedGroups = licensingGroups.filter(
    (group) => !group.parent.isAnneaOnly && !isSubscribed(group.parent) && !group.parent.isFree
  );
  const leafApps = [
    ...standaloneApps,
    ...groups.flatMap((group) => group.children),
  ];
  const favoriteApps = leafApps.filter(
    (app) => isFavorite(app) && (isAccessible(app) || (app.isAnneaOnly && !app.isAdminGranted))
  );
  const anneaApps = standaloneApps.filter((app) => app.isAnneaOnly);
  const subscribedApps = standaloneApps.filter(
    (app) => !app.isAnneaOnly && isSubscribed(app) && !app.isFree
  );
  const freeApps = standaloneApps.filter((app) => !app.isAnneaOnly && app.isFree);
  const unsubscribedApps = standaloneApps.filter((app) => !app.isAnneaOnly && !isSubscribed(app) && !app.isFree);
  const hasVisibleResults =
    catalogGroups.length > 0 ||
    anneaGroups.length > 0 ||
    anneaApps.length > 0 ||
    subscribedGroups.length > 0 ||
    subscribedApps.length > 0 ||
    freeGroups.length > 0 ||
    freeApps.length > 0 ||
    unsubscribedGroups.length > 0 ||
    unsubscribedApps.length > 0;

  const renderLeafCard = (app: App, accessible: boolean) => (
    <Col xs={24} sm={12} lg={6} key={app.id}>
      <AppCard
        app={app}
        iconColor={appIconColorMap.get(app.id)}
        onCardClick={accessible || (app.isAnneaOnly && !app.isAdminGranted) ? () => openAppLink(app) : undefined}
        isFavorite={isFavorite(app)}
        onToggleFavorite={accessible || (app.isAnneaOnly && !app.isAdminGranted) ? () => toggleFavorite(app) : undefined}
        actions={
          accessible && app.isTrial && (!app.parentId || app.licenseAppId === app.id)
            ? [renderPlanButton(app)]
            : !accessible && (!app.isAnneaOnly || app.isAdminGranted)
            ? [renderSubscribeButton(app)].filter(Boolean) as React.ReactNode[]
            : undefined
        }
      />
    </Col>
  );

  const renderGroup = (parent: App, children: App[], subscribed: boolean) => {
    const groupColor = parent.iconColor || getAutoColor(parent.id);
    return (
      <AppGroup
        key={parent.id}
        parent={parent}
        subscribed={parent.isCatalogGroup ? false : parent.isAdminGranted ? isAccessible(parent) : subscribed}
        expandByDefault={parent.isCatalogGroup || !subscribed}
        actions={parent.isCatalogGroup ? null : subscribed && parent.isTrial ? renderPlanButton(parent) : renderSubscribeButton(parent) || <></>}
      >
        <Row gutter={[cardGutter, cardGutter]}>
          {children.map((child) => {
            const childAccessible = parent.isCatalogGroup
              ? isAccessible(child) || Boolean(child.isAnneaOnly && !child.isAdminGranted)
              : child.isAdminGranted ? isAccessible(child) : subscribed || Boolean(child.isFree || child.isAnneaOnly || parent.isAnneaOnly);
            let childActions: React.ReactNode[] | undefined;
            if (parent.isCatalogGroup) {
              if (childAccessible && child.isTrial) childActions = [renderPlanButton(child)];
              else if (!childAccessible) childActions = [renderSubscribeButton(child)].filter(Boolean) as React.ReactNode[];
            }
            return (
              <Col xs={24} sm={12} lg={6} key={child.id}>
                <AppCard
                  app={child}
                  iconColor={groupColor}
                  actions={childActions}
                  onCardClick={childAccessible ? () => openAppLink(child) : undefined}
                  isFavorite={isFavorite(child)}
                  onToggleFavorite={childAccessible ? () => toggleFavorite(child) : undefined}
                />
              </Col>
            );
          })}
        </Row>
      </AppGroup>
    );
  };

  const sectionStyle: React.CSSProperties = {
    border: '1px solid #f0f0f0',
    borderRadius: 12,
    background: '#fff',
    overflow: 'hidden',
    borderLeft: '4px solid #f46524',
  };
  const sectionTitleStyle: React.CSSProperties = {
    background: '#fff5f0',
    padding: '10px 16px',
    borderBottom: '1px solid #f0f0f0',
  };
  const sectionTitleTextStyle: React.CSSProperties = {
    margin: 0,
  };
  const sectionBodyStyle: React.CSSProperties = {
    padding: sectionPadding,
  };
  const sectionHeader = (title: string) => (
    <div style={sectionTitleStyle}>
      <Title level={4} style={sectionTitleTextStyle}>{title}</Title>
    </div>
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Title level={2} style={{ margin: 0 }}>
          Apps
        </Title>
        <Input
          allowClear
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search apps by name"
          style={{ width: 320, maxWidth: '100%' }}
        />
      </div>

      {loading ? (
        <Row gutter={[cardGutter, cardGutter]}>
          {[1, 2, 3].map((i) => (
            <Col xs={24} sm={12} lg={6} key={i}>
              <Card loading style={{ borderRadius: 12 }} />
            </Col>
          ))}
        </Row>
      ) : apps.length === 0 ? (
        <Empty
          image={<AppstoreOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
          description="No apps available yet"
        />
      ) : !hasVisibleResults ? (
        <Empty
          image={<AppstoreOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
          description="No apps match your search"
        />
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {favoriteApps.length > 0 && (
            <div style={sectionStyle}>
              <Collapse
                bordered={false}
                defaultActiveKey={['favorites']}
                items={[
                  {
                    key: 'favorites',
                    label: sectionHeader('Favorites'),
                    children: (
                      <div style={sectionBodyStyle}>
                        <Row gutter={[cardGutter, cardGutter]}>
                          {favoriteApps.map((app) => renderLeafCard(app, true))}
                        </Row>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          )}

          {subscribedGroups.length > 0 || subscribedApps.length > 0 ? (
            <div style={sectionStyle}>
              <Collapse
                bordered={false}
                defaultActiveKey={['subscribed']}
                items={[
                  {
                    key: 'subscribed',
                    label: sectionHeader('Subscribed Apps'),
                    children: (
                      <div style={sectionBodyStyle}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          {subscribedGroups.map((group) =>
                            renderGroup(group.parent, group.children, true)
                          )}

                          {subscribedApps.length > 0 && (
                            <Row gutter={[cardGutter, cardGutter]}>
                              {subscribedApps.map((app) => renderLeafCard(app, true))}
                            </Row>
                          )}
                        </Space>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          ) : null}

          {catalogGroups.map((group) => renderGroup(group.parent, group.children, false))}

          {(freeGroups.length > 0 || freeApps.length > 0) && (
            <div style={sectionStyle}>
              <Collapse
                bordered={false}
                defaultActiveKey={['free']}
                items={[
                  {
                    key: 'free',
                    label: sectionHeader('Free Apps'),
                    children: (
                      <div style={sectionBodyStyle}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          {freeGroups.map((group) =>
                            renderGroup(group.parent, group.children, true)
                          )}
                          <Row gutter={[cardGutter, cardGutter]}>
                            {freeApps.map((app) => renderLeafCard(app, true))}
                          </Row>
                        </Space>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          )}

          {(unsubscribedGroups.length > 0 || unsubscribedApps.length > 0) && (
            <div style={sectionStyle}>
              <Collapse
                bordered={false}
                defaultActiveKey={['explore']}
                items={[
                  {
                    key: 'explore',
                    label: sectionHeader('Subscription Based Apps'),
                    children: (
                      <div style={sectionBodyStyle}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          {unsubscribedGroups.map((group) =>
                            renderGroup(group.parent, group.children, false)
                          )}

                          <Row gutter={[cardGutter, cardGutter]}>
                            {unsubscribedApps.map((app) => renderLeafCard(app, false))}
                          </Row>
                        </Space>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          )}

          {(anneaGroups.length > 0 || anneaApps.length > 0) && (
            <div style={sectionStyle}>
              <Collapse
                bordered={false}
                defaultActiveKey={['annea']}
                items={[
                  {
                    key: 'annea',
                    label: sectionHeader('Annea Apps'),
                    children: (
                      <div style={sectionBodyStyle}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                          {anneaGroups.map((group) =>
                            renderGroup(group.parent, group.children, true)
                          )}
                          <Row gutter={[cardGutter, cardGutter]}>
                            {anneaApps.map((app) => renderLeafCard(app, app.isAdminGranted ? isAccessible(app) : true))}
                          </Row>
                        </Space>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          )}
        </Space>
      )}

      <Modal
        title={`Subscribe to ${checkoutApp?.name || ''}`}
        open={checkoutOpen}
        onOk={handleCheckout}
        onCancel={() => setCheckoutOpen(false)}
        okText={selectedPriceId === LOCAL_TRIAL_OPTION ? `Start ${trialDays}-day trial` : 'Subscribe'}
        confirmLoading={checkoutLoading}
        destroyOnHidden
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {orgs.some((org) => org.role === 'OWNER' || org.role === 'ADMIN') && (
            <div>
              <Text type="secondary">Subscription for</Text>
              <Radio.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
                value={billingScope}
                onChange={(event) => {
                  const nextScope = event.target.value as BillingScope;
                  setBillingScope(nextScope);
                  if (nextScope === 'organization') {
                    const adminOrgs = orgs.filter((org) => org.role === 'OWNER' || org.role === 'ADMIN');
                    const defaultOrg = adminOrgs.find((org) => !org.manualInvoicing) || adminOrgs[0];
                    setSelectedOrgId(defaultOrg?.id || null);
                    setSelectedPriceId(checkoutApp?.orgTrialEligible ? LOCAL_TRIAL_OPTION : checkoutApp?.prices?.[0]?.id || null);
                  } else {
                    setSelectedOrgId(null);
                    setSelectedPriceId(checkoutApp?.trialEligible ? LOCAL_TRIAL_OPTION : checkoutApp?.prices?.[0]?.id || null);
                  }
                }}
              >
                <Radio value="personal">Personal</Radio>
                <Radio value="organization">Company</Radio>
              </Radio.Group>
            </div>
          )}

          {billingScope === 'organization' && (
            <div>
              <Text type="secondary">Company</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                options={orgs
                  .filter((org) => org.role === 'OWNER' || org.role === 'ADMIN')
                  .map((org) => ({
                    value: org.id,
                    label: org.manualInvoicing ? `${org.name} (manual invoicing)` : org.name,
                    disabled: org.manualInvoicing,
                  }))}
                value={selectedOrgId || undefined}
                onChange={(value) => setSelectedOrgId(value || null)}
              />
              {orgs.some(
                (org) =>
                  (org.role === 'OWNER' || org.role === 'ADMIN') && org.manualInvoicing
              ) && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Manual invoicing organizations must be provisioned by an admin.
                </Text>
              )}
            </div>
          )}

          <div>
            <Text strong>Free Trial</Text>
            <Radio.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
              value={selectedPriceId || undefined}
              onChange={(event) => setSelectedPriceId(event.target.value)}
            >
              {trialState.canStartTrial && (
                <Radio value={LOCAL_TRIAL_OPTION}>{trialDays}-day trial - Free</Radio>
              )}
            </Radio.Group>
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Trial does not start a paid subscription automatically. You can choose a paid plan later.
            </Text>
            {trialState.hasActiveTrial && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {trialState.activeTrialEnd
                  ? `Your free trial for this app is active until ${trialState.activeTrialEnd}. You can choose a paid plan now, or continue using the trial until it ends.`
                  : 'Your free trial for this app is active. You can choose a paid plan now, or continue using the trial.'}
              </Text>
            )}
            {trialState.hasExpiredTrial && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {billingScope === 'organization'
                  ? "Your organization's previous free trial for this app has ended. Select a plan below to continue, or contact us at info@annea.sk if you need more time to evaluate."
                  : 'Your previous free trial for this app has ended. Select a plan below to continue, or contact us at info@annea.sk if you need more time to evaluate.'}
              </Text>
            )}
          </div>

          <div>
            <Text strong>Subscription Options</Text>
            <Radio.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
              value={selectedPriceId || undefined}
              onChange={(event) => setSelectedPriceId(event.target.value)}
            >
              {(checkoutApp?.prices || []).map((price) => (
                <Radio key={price.id} value={price.id}>
                  {price.name || price.interval} - {formatAmount(price.unitAmount, price.currency)}
                </Radio>
              ))}
            </Radio.Group>
          </div>

          {billingScope === 'organization' && selectedOrgId && (
            <div>
              <Text type="secondary">Seats (users)</Text>
              <InputNumber
                min={1}
                value={seatQuantity}
                onChange={(value) => setSeatQuantity(value ? Number(value) : 1)}
                style={{ width: '100%', marginTop: 8 }}
              />
            </div>
          )}

          <Text type="secondary">
            If you or your organization need an alternative payment method, contact us at info@annea.sk.
          </Text>
          {selectedPriceId !== LOCAL_TRIAL_OPTION && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Secure payment powered by Stripe.
            </Text>
          )}
        </Space>
      </Modal>
      <Modal
        title="Welcome to Annea Apps Portal!"
        open={welcomeOpen}
        onCancel={() => setWelcomeOpen(false)}
        footer={[
          <Button key="start" type="primary" onClick={() => setWelcomeOpen(false)}>
            Let's get started
          </Button>,
        ]}
      >
        <Space direction="vertical" size="small">
          <Text>
            We're glad you're here. Eligible subscription apps come with a free {trialDays}-day trial so you can explore at your own pace, no commitment needed.
          </Text>
          <Text>
            Some apps require access granted by an administrator. When you're ready to continue after an eligible trial, you can easily activate a subscription directly within each app. Have questions or need more time to evaluate? We're always happy to help - just reach out at info@annea.sk.
          </Text>
        </Space>
      </Modal>
    </Space>
  );
}
